import type { CalendarAccommodation, CalendarUnit, CalendarBooking, DragState, TweakPrefs } from './types';
import { toYmd } from './pricing';
import { getCellState, buildBandInfos, buildFreeSegments, priceTier } from './bands';
import { getDayPrice } from './pricing';
import { onCellMouseDown, onCellMouseEnter } from './drag';
import { openBookingModal } from './modals';

export interface GridRenderOptions {
    container: HTMLElement;
    accoms: CalendarAccommodation[];
    units: CalendarUnit[];
    bookings: CalendarBooking[];
    days: Date[];
    todayYmd: string;
    prefs: TweakPrefs;
    i18n: Record<string, string>;
    drag: DragState | null;
    visibleStates: Set<string>;
    activeOnly: boolean;
    hasPriceOnly: boolean;
    search: string;
    typeFilter: Set<string>;
    serviceFilter: Set<string>;
    classifFilter: Set<string>;
    priceMin: number;
    priceMax: number;
    cellW: number;
    labelW: number;
    currency: string;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function renderGrid(opts: GridRenderOptions): void {
    const {
        container, accoms, units, bookings, days, todayYmd, prefs,
        i18n, drag, visibleStates, activeOnly, hasPriceOnly, search,
        typeFilter, serviceFilter, classifFilter, priceMin, priceMax,
        cellW, labelW, currency,
    } = opts;

    const windowFrom = toYmd(days[0]);
    const windowTo = toYmd(days[days.length - 1]);
    const totalW = cellW * days.length;

    const filteredUnits = units.filter(u => {
        if (activeOnly && !u.active) return false;
        if (search && !(u.name ?? '').toLowerCase().includes(search.toLowerCase())) return false;
        if (typeFilter.size > 0 && !typeFilter.has(u.type ?? '')) return false;
        if (serviceFilter.size > 0 && !serviceFilter.has(u.service ?? '')) return false;
        if (classifFilter.size > 0 && !classifFilter.has(u.classification ?? '')) return false;
        if (hasPriceOnly) {
            const hasPricePeriod = days.some(d => getDayPrice(u.id, toYmd(d)) != null);
            if (!hasPricePeriod) return false;
        }
        return true;
    });

    const todayIdx = days.findIndex(d => toYmd(d) === todayYmd);

    container.innerHTML = '';
    container.className = `tl-grid density-${prefs.density}${prefs.showWeekends ? ' show-weekends' : ''}`;
    container.style.gridTemplateColumns = `${labelW}px ${totalW}px`;

    // Build month spans for the header
    const monthSpans: Array<{ label: string; count: number }> = [];
    for (const d of days) {
        const lbl = `${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`;
        if (monthSpans.length > 0 && monthSpans[monthSpans.length - 1].label === lbl) {
            monthSpans[monthSpans.length - 1].count++;
        } else {
            monthSpans.push({ label: lbl, count: 1 });
        }
    }

    // Corner (col 1, row 1 of the CSS grid)
    const corner = document.createElement('div');
    corner.className = 'tl-corner';
    const cornerLabel = document.createElement('div');
    cornerLabel.className = 'tl-corner-label';
    cornerLabel.textContent = i18n['unitAccommodation'] || 'Unit / Accommodation';
    const cornerSub = document.createElement('div');
    cornerSub.className = 'tl-corner-sub';
    cornerSub.textContent = `${filteredUnits.length} ${i18n['unitsShown'] || 'units shown'}`;
    corner.appendChild(cornerLabel);
    corner.appendChild(cornerSub);
    container.appendChild(corner);

    // Head (col 2, row 1) — month strip + day strip
    const head = document.createElement('div');
    head.className = 'tl-head';

    const months = document.createElement('div');
    months.className = 'tl-months';
    months.style.width = `${totalW}px`;
    for (const ms of monthSpans) {
        const cell = document.createElement('div');
        cell.className = 'tl-month-cell';
        cell.style.width = `${cellW * ms.count}px`;
        cell.textContent = ms.label;
        months.appendChild(cell);
    }
    head.appendChild(months);

    const daysRow = document.createElement('div');
    daysRow.className = 'tl-days';
    daysRow.style.width = `${totalW}px`;
    for (let i = 0; i < days.length; i++) {
        const d = days[i];
        const dow = d.getDay();
        const isWknd = dow === 0 || dow === 6;
        const isToday = toYmd(d) === todayYmd;
        const cell = document.createElement('div');
        cell.className = `tl-day-head${isWknd ? ' weekend' : ''}${isToday ? ' today' : ''}`;
        cell.style.width = `${cellW}px`;
        const dowSpan = document.createElement('span');
        dowSpan.className = 'tl-dow';
        dowSpan.textContent = DOW_SHORT[dow];
        const dnumSpan = document.createElement('span');
        dnumSpan.className = 'tl-dnum';
        dnumSpan.textContent = String(d.getDate());
        cell.appendChild(dowSpan);
        cell.appendChild(dnumSpan);
        daysRow.appendChild(cell);
    }
    head.appendChild(daysRow);
    container.appendChild(head);

    // Group units by accommodation
    const accGroup = new Map<number, CalendarUnit[]>();
    for (const u of filteredUnits) {
        if (!accGroup.has(u.accomId)) accGroup.set(u.accomId, []);
        accGroup.get(u.accomId)!.push(u);
    }

    let altIdx = 0;
    for (const accom of accoms) {
        const unitsForAccom = accGroup.get(accom.id);
        if (!unitsForAccom || unitsForAccom.length === 0) continue;

        if (prefs.groupByAccom) {
            // Accom label (col 1) + accom row (col 2)
            const accomLabel = document.createElement('div');
            accomLabel.className = 'tl-unit-label accom-row';
            accomLabel.textContent = accom.name ?? '—';
            container.appendChild(accomLabel);

            const accomRow = document.createElement('div');
            accomRow.className = 'tl-row accom-row';
            accomRow.style.width = `${totalW}px`;
            for (let i = 0; i < days.length; i++) {
                const d = days[i];
                const dow = d.getDay();
                const cell = document.createElement('div');
                cell.className = `tl-cell${dow === 0 || dow === 6 ? ' weekend' : ''}`;
                cell.style.width = `${cellW}px`;
                accomRow.appendChild(cell);
            }
            if (todayIdx >= 0) {
                const line = document.createElement('div');
                line.className = 'tl-today-line';
                line.style.left = `${todayIdx * cellW}px`;
                accomRow.appendChild(line);
            }
            container.appendChild(accomRow);
        }

        for (const unit of unitsForAccom) {
            const isAlt = altIdx % 2 === 1;
            altIdx++;

            // Unit label (col 1)
            container.appendChild(buildUnitLabel(unit, isAlt, accom));

            // Unit row (col 2)
            container.appendChild(buildUnitRow(
                unit, accom, days, bookings, todayYmd, windowFrom, windowTo,
                prefs, i18n, drag, visibleStates, priceMin, priceMax,
                cellW, totalW, todayIdx, isAlt, currency,
            ));
        }
    }
}

function buildUnitLabel(unit: CalendarUnit, isAlt: boolean, accom: CalendarAccommodation): HTMLElement {
    const label = document.createElement('div');
    label.className = `tl-unit-label${isAlt ? ' alt' : ''}${!unit.active ? ' unit-inactive' : ''}${accom.isPartner ? ' partner' : ''}`;

    const uname = document.createElement('div');
    uname.className = 'tl-uname';
    uname.textContent = unit.name ?? '—';
    label.appendChild(uname);

    const umeta = document.createElement('div');
    umeta.className = 'tl-umeta';
    for (const tag of [unit.type, unit.service, unit.classification].filter(Boolean)) {
        const chip = document.createElement('span');
        chip.className = 'tl-utag';
        chip.textContent = tag!;
        umeta.appendChild(chip);
    }
    if (accom.isPartner && accom.partnerAgencyName) {
        const badge = document.createElement('span');
        badge.className = 'partner-badge';
        badge.textContent = `Partner · ${accom.partnerAgencyName}`;
        umeta.appendChild(badge);
    }
    label.appendChild(umeta);

    return label;
}

function buildUnitRow(
    unit: CalendarUnit,
    accom: CalendarAccommodation,
    days: Date[],
    bookings: CalendarBooking[],
    todayYmd: string,
    windowFrom: string,
    windowTo: string,
    prefs: TweakPrefs,
    i18n: Record<string, string>,
    drag: DragState | null,
    visibleStates: Set<string>,
    priceMin: number,
    priceMax: number,
    cellW: number,
    totalW: number,
    todayIdx: number,
    isAlt: boolean,
    currency: string,
): HTMLElement {
    const row = document.createElement('div');
    row.className = `tl-row${isAlt ? ' alt' : ''}${!unit.active ? ' unit-inactive' : ''}${accom.isPartner ? ' partner' : ''}`;
    row.dataset.unitId = String(unit.id);
    row.style.width = `${totalW}px`;

    const isDragging = drag?.unitId === unit.id;

    // Pre-compute which day indices are covered by a visible free-band so
    // per-cell price numbers are not rendered on top of the band's label.
    const freeSegs = buildFreeSegments(unit.id, days, windowFrom, windowTo);
    const visibleFreeSegs = freeSegs.filter(seg => seg.perNight != null);
    const coveredByBand = new Set<number>();
    for (const seg of visibleFreeSegs) {
        const bWidth = (seg.endIdx - seg.startIdx) * cellW - 4;
        if (bWidth <= 12) continue;
        for (let i = seg.startIdx; i < seg.endIdx; i++) coveredByBand.add(i);
    }

    // Day cells
    for (let i = 0; i < days.length; i++) {
        const d = days[i];
        const ymd = toYmd(d);
        const dow = d.getDay();
        const isWknd = dow === 0 || dow === 6;
        const state = getCellState(unit.id, ymd, todayYmd, bookings);
        const inDragRange = isDragging
            ? (i >= Math.min(drag!.startIdx, drag!.currIdx) && i <= Math.max(drag!.startIdx, drag!.currIdx))
            : false;

        const cell = document.createElement('div');
        cell.className = `tl-cell ${state}${isWknd ? ' weekend' : ''}${inDragRange ? ' drag-selected' : ''}`;
        cell.style.width = `${cellW}px`;
        cell.dataset.dayIdx = String(i);

        if (state === 'free' && prefs.priceDisplay !== 'hover') {
            const price = getDayPrice(unit.id, ymd);
            if (price != null) {
                if ((prefs.priceDisplay === 'number' || prefs.priceDisplay === 'both') && !coveredByBand.has(i)) {
                    const priceEl = document.createElement('span');
                    priceEl.className = 'cell-price';
                    priceEl.textContent = String(price);
                    cell.appendChild(priceEl);
                }
                if ((prefs.priceDisplay === 'heat' || prefs.priceDisplay === 'both') && priceMin < priceMax) {
                    const tier = priceTier(price, priceMin, priceMax);
                    cell.classList.add(`heat-${tier}`);
                }
            }
        }

        cell.addEventListener('mousedown', e => {
            e.preventDefault();
            onCellMouseDown(unit.id, i, state);
        });
        cell.addEventListener('mouseenter', () => onCellMouseEnter(unit.id, i));

        row.appendChild(cell);
    }

    // Free price-period bands overlay (uses pre-computed freeSegs / visibleFreeSegs above)
    if (visibleFreeSegs.length > 0 && prefs.priceDisplay !== 'hover') {
        const freeOverlay = document.createElement('div');
        freeOverlay.className = 'tl-row-overlay';
        for (const seg of visibleFreeSegs) {
            const bLeft = seg.startIdx * cellW + 2;
            const bWidth = (seg.endIdx - seg.startIdx) * cellW - 4;
            if (bWidth <= 12) continue;
            const el = document.createElement('div');
            el.className = `tl-band free-band${accom.isPartner ? ' partner' : ''}`;
            el.style.left = `${bLeft}px`;
            el.style.width = `${bWidth}px`;
            const nightsLabel = seg.nights === 1
                ? (i18n['night'] || 'night')
                : (i18n['freeNights'] || 'nights');
            el.innerHTML = `<span class="tl-band-label">${seg.perNight} ${currency} / ${i18n['night'] || 'night'} · ${seg.nights} ${nightsLabel}</span>`;
            freeOverlay.appendChild(el);
        }
        if (freeOverlay.hasChildNodes()) row.appendChild(freeOverlay);
    }

    // Booking bands overlay
    const bands = buildBandInfos(unit.id, days, bookings);
    const visibleBands = bands.filter(b =>
        visibleStates.size === 0 || visibleStates.has(b.booking.status)
    );
    if (visibleBands.length > 0) {
        const bookingOverlay = document.createElement('div');
        bookingOverlay.className = 'tl-row-overlay';
        for (const band of visibleBands) {
            const bLeft = band.startIdx * cellW + 2;
            const bWidth = (band.endIdx - band.startIdx) * cellW - 4;
            if (bWidth <= 0) continue;
            const el = buildBandEl(band.booking, band.startIdx, band.endIdx, prefs.showGuestNames, i18n, accom.isPartner);
            el.style.left = `${bLeft}px`;
            el.style.width = `${bWidth}px`;
            el.addEventListener('mousedown', e => e.stopPropagation());
            el.addEventListener('click', e => {
                e.stopPropagation();
                openBookingModal(band.booking);
            });
            bookingOverlay.appendChild(el);
        }
        if (bookingOverlay.hasChildNodes()) row.appendChild(bookingOverlay);
    }

    // Drag selection band
    if (isDragging && drag) {
        const minIdx = Math.min(drag.startIdx, drag.currIdx);
        const maxIdx = Math.max(drag.startIdx, drag.currIdx);
        const sel = document.createElement('div');
        sel.className = 'tl-selection';
        sel.style.left = `${minIdx * cellW}px`;
        sel.style.width = `${(maxIdx - minIdx + 1) * cellW}px`;
        row.appendChild(sel);
    }

    // Today line
    if (todayIdx >= 0) {
        const line = document.createElement('div');
        line.className = 'tl-today-line';
        line.style.left = `${todayIdx * cellW}px`;
        row.appendChild(line);
    }

    return row;
}

function buildBandEl(
    booking: CalendarBooking,
    startIdx: number,
    endIdx: number,
    showGuestNames: boolean,
    i18n: Record<string, string>,
    isPartner: boolean = false,
): HTMLElement {
    const el = document.createElement('div');
    el.className = `tl-band ${booking.status}${isPartner ? ' partner' : ''}`;
    el.title = showGuestNames
        ? `${booking.guest} · ${i18n['contractId'] || 'Contract'} #${booking.contractId}`
        : `${i18n['contractId'] || 'Contract'} #${booking.contractId}`;

    if (showGuestNames && endIdx - startIdx > 1) {
        const label = document.createElement('span');
        label.className = 'tl-band-label';
        const init = document.createElement('span');
        init.className = 'tl-band-init';
        init.textContent = initials(booking.guest);
        label.appendChild(init);
        label.appendChild(document.createTextNode(` ${esc(booking.guest)}`));
        el.appendChild(label);
    }
    return el;
}

function initials(name: string): string {
    return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
}

function esc(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
